import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Separator,
	Textarea,
} from "@monbits/ui";
import {
	Ban,
	Check,
	CheckCircle,
	Clock,
	Copy,
	ExternalLink,
	XCircle,
} from "lucide-react";
import * as React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { EntityDrawer } from "src/components/entity-drawer";
import TimeLabel from "src/components/time-label/time-label";
import {
	useApprovePoolWithdrawal,
	useCancelPoolWithdrawal,
	useRejectPoolWithdrawal,
} from "src/queries/pool-withdrawal-queries";
import type { PoolWithdrawalsOutletContext } from "./pool-withdrawals-layout";
import { statusConfig } from "./status-config";
export const PoolWithdrawalDetail: React.FC = () => {
	const { withdrawalId } = useParams<{ withdrawalId: string }>();
	const withdrawalIdNum = withdrawalId
		? Number.parseInt(withdrawalId, 10)
		: null;

	const outletContext = useOutletContext<
		PoolWithdrawalsOutletContext | undefined
	>();
	const withdrawals = outletContext?.withdrawals ?? [];
	const isLoading = outletContext?.isLoading ?? false;
	const withdrawal = React.useMemo(() => {
		if (withdrawalIdNum === null) {
			return null;
		}
		return withdrawals.find((item) => item.id === withdrawalIdNum) ?? null;
	}, [withdrawals, withdrawalIdNum]);

	const [showApproveDialog, setShowApproveDialog] = React.useState(false);
	const [showRejectDialog, setShowRejectDialog] = React.useState(false);
	const [showCancelDialog, setShowCancelDialog] = React.useState(false);

	const [approvalNotes, setApprovalNotes] = React.useState("");
	const [rejectReason, setRejectReason] = React.useState("");
	const [cancelReason, setCancelReason] = React.useState("");
	const [totpCode, setTotpCode] = React.useState("");

	const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);

	const approveMutation = useApprovePoolWithdrawal();
	const rejectMutation = useRejectPoolWithdrawal();
	const cancelMutation = useCancelPoolWithdrawal();

	const resetForms = () => {
		setApprovalNotes("");
		setRejectReason("");
		setCancelReason("");
		setTotpCode("");
	};

	const handleOpenApproveDialog = () => {
		resetForms();
		setShowApproveDialog(true);
	};

	const handleCloseApproveDialog = () => {
		setShowApproveDialog(false);
		resetForms();
	};

	const handleApprove = async () => {
		if (!withdrawal) return;

		if (totpCode.trim().length !== 6) {
			toast.error("Invalid 2FA code");
			return;
		}

		try {
			await approveMutation.mutateAsync({
				withdrawalId: withdrawal.id,
				notes: approvalNotes.trim() || undefined,
				totpCode: totpCode.trim(),
			});
			toast.success("Withdrawal approved");
			handleCloseApproveDialog();
		} catch (error: unknown) {
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to approve withdrawal";
			toast.error(errorMessage);
		}
	};

	const handleOpenRejectDialog = () => {
		resetForms();
		setShowRejectDialog(true);
	};

	const handleCloseRejectDialog = () => {
		setShowRejectDialog(false);
		resetForms();
	};

	const handleReject = async () => {
		if (!withdrawal) return;

		if (rejectReason.trim().length < 10) {
			toast.error("Reason must be at least 10 characters");
			return;
		}

		try {
			await rejectMutation.mutateAsync({
				withdrawalId: withdrawal.id,
				reason: rejectReason.trim(),
			});
			toast.success("Withdrawal rejected");
			handleCloseRejectDialog();
		} catch (error: unknown) {
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to reject withdrawal";
			toast.error(errorMessage);
		}
	};

	const handleOpenCancelDialog = () => {
		resetForms();
		setShowCancelDialog(true);
	};

	const handleCloseCancelDialog = () => {
		setShowCancelDialog(false);
		resetForms();
	};

	const handleCancel = async () => {
		if (!withdrawal) return;

		try {
			await cancelMutation.mutateAsync({
				withdrawalId: withdrawal.id,
				reason: cancelReason.trim() || undefined,
			});
			toast.success("Withdrawal cancelled");
			handleCloseCancelDialog();
		} catch (error: unknown) {
			const errorMessage =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to cancel withdrawal";
			toast.error(errorMessage);
		}
	};

	const handleCopyAddress = (address: string, e: React.MouseEvent) => {
		e.stopPropagation();
		navigator.clipboard.writeText(address);
		setCopiedAddress(address);
		toast.success("Address copied");
		setTimeout(() => setCopiedAddress(null), 2000);
	};

	const truncateAddress = (addr: string) =>
		`${addr.slice(0, 8)}...${addr.slice(-6)}`;

	const canApprove =
		withdrawal?.status === "pending_approval" && withdrawal?.requiresApproval;
	const canReject =
		withdrawal?.status === "pending_approval" && withdrawal?.requiresApproval;
	const canCancel =
		withdrawal?.status === "pending_approval" ||
		withdrawal?.status === "approved";

	return (
		<>
			<EntityDrawer
				title={`Pool Withdrawal #${withdrawalId}`}
				parentPath="/pool-withdrawals"
				isLoading={isLoading}
				isNotFound={!isLoading && !withdrawal}
				notFoundMessage="Pool withdrawal not found"
			>
				{withdrawal && (
					<>
						<div className="flex-1 overflow-y-auto p-6">
							<div className="space-y-6">
								<div className="space-y-3">
									<h4 className="text-sm font-medium text-muted-foreground">
										Status
									</h4>
									{(() => {
										const config = statusConfig[withdrawal.status] ?? {
											label: withdrawal.status,
											type: "default" as const,
											icon: Clock,
										};
										return (
											<div className="flex items-center gap-2">
												{React.createElement(config.icon, {
													className: "h-4 w-4",
												})}
												<span className="text-sm font-medium">
													{config.label}
												</span>
											</div>
										);
									})()}
									{withdrawal.failureReason && (
										<p className="text-sm text-red-600">
											{withdrawal.failureReason}
										</p>
									)}
									{withdrawal.rejectionReason && (
										<p className="text-sm text-red-600">
											{withdrawal.rejectionReason}
										</p>
									)}
								</div>

								<Separator />

								<div className="space-y-3">
									<h4 className="text-sm font-medium text-muted-foreground">
										Amount
									</h4>
									<div className="flex items-center gap-3">
										<CryptoIcon
											ticker={withdrawal.currency}
											className="h-8 w-8"
										/>
										<div>
											<p className="text-2xl font-bold">
												{withdrawal.amount} {withdrawal.currency}
											</p>
											<p className="text-sm text-muted-foreground">
												${withdrawal.usdAmount} USD
											</p>
										</div>
									</div>
								</div>

								<Separator />

								<div className="space-y-3">
									<h4 className="text-sm font-medium text-muted-foreground">
										Addresses
									</h4>
									<div className="space-y-2">
										<div className="flex justify-between items-center">
											<span className="text-sm text-muted-foreground">
												From
											</span>
											<div className="flex items-center gap-1">
												<span className="font-mono text-sm">
													{truncateAddress(withdrawal.walletAddress)}
												</span>
												<Button
													variant="ghost"
													size="icon"
													className="h-6 w-6"
													onClick={(e) =>
														handleCopyAddress(withdrawal.walletAddress, e)
													}
												>
													{copiedAddress === withdrawal.walletAddress ? (
														<Check className="h-3 w-3 text-green-600" />
													) : (
														<Copy className="h-3 w-3" />
													)}
												</Button>
											</div>
										</div>
										<div className="flex justify-between items-center">
											<span className="text-sm text-muted-foreground">To</span>
											<div className="flex items-center gap-1">
												<span className="font-mono text-sm">
													{truncateAddress(withdrawal.recipientAddress)}
												</span>
												<Button
													variant="ghost"
													size="icon"
													className="h-6 w-6"
													onClick={(e) =>
														handleCopyAddress(withdrawal.recipientAddress, e)
													}
												>
													{copiedAddress === withdrawal.recipientAddress ? (
														<Check className="h-3 w-3 text-green-600" />
													) : (
														<Copy className="h-3 w-3" />
													)}
												</Button>
											</div>
										</div>
									</div>
								</div>

								<Separator />

								<div className="space-y-3">
									<h4 className="text-sm font-medium text-muted-foreground">
										Purpose
									</h4>
									<p className="text-sm">{withdrawal.purpose}</p>
								</div>

								{withdrawal.approval && (
									<>
										<Separator />
										<div className="space-y-3">
											<h4 className="text-sm font-medium text-muted-foreground">
												Approval
											</h4>
											<div className="space-y-2 text-sm">
												<div className="flex justify-between">
													<span className="text-muted-foreground">Status</span>
													<span>{withdrawal.approval.status}</span>
												</div>
												{withdrawal.approval.approvedBy && (
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															Approved By
														</span>
														<span>{withdrawal.approval.approvedBy.email}</span>
													</div>
												)}
												{withdrawal.approval.approvedAt && (
													<div className="flex justify-between">
														<span className="text-muted-foreground">
															Approved At
														</span>
														<TimeLabel time={withdrawal.approval.approvedAt} />
													</div>
												)}
												{withdrawal.approval.notes && (
													<div className="flex justify-between">
														<span className="text-muted-foreground">Notes</span>
														<span>{withdrawal.approval.notes}</span>
													</div>
												)}
											</div>
										</div>
									</>
								)}

								{withdrawal.transactionHash && (
									<>
										<Separator />
										<div className="space-y-3">
											<h4 className="text-sm font-medium text-muted-foreground">
												Transaction
											</h4>
											<div className="flex items-center gap-2">
												<span className="font-mono text-sm">
													{truncateAddress(withdrawal.transactionHash)}
												</span>
												<Button
													variant="ghost"
													size="icon"
													className="h-6 w-6"
													onClick={(e) => {
														if (withdrawal.transactionHash) {
															handleCopyAddress(withdrawal.transactionHash, e);
														}
													}}
												>
													{copiedAddress === withdrawal.transactionHash ? (
														<Check className="h-3 w-3 text-green-600" />
													) : (
														<Copy className="h-3 w-3" />
													)}
												</Button>
												<Button size="sm" variant="ghost" asChild>
													<a
														href={`https://blockscan.com/tx/${withdrawal.transactionHash}`}
														target="_blank"
														rel="noopener noreferrer"
													>
														<ExternalLink className="h-3 w-3" />
													</a>
												</Button>
											</div>
										</div>
									</>
								)}

								<Separator />

								<div className="space-y-3">
									<h4 className="text-sm font-medium text-muted-foreground">
										Details
									</h4>
									<div className="space-y-2 text-sm">
										<div className="flex justify-between">
											<span className="text-muted-foreground">Created By</span>
											<span>{withdrawal.createdBy.email}</span>
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground">Created</span>
											<TimeLabel time={withdrawal.createdAt} />
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground">Updated</span>
											<TimeLabel time={withdrawal.updatedAt} />
										</div>
										{withdrawal.completedAt && (
											<div className="flex justify-between">
												<span className="text-muted-foreground">Completed</span>
												<TimeLabel time={withdrawal.completedAt} />
											</div>
										)}
									</div>
								</div>
							</div>
						</div>

						<Separator />

						<div className="flex-shrink-0 p-6 space-y-2">
							{canApprove && (
								<Button onClick={handleOpenApproveDialog} className="w-full">
									<CheckCircle className="mr-2 h-4 w-4" />
									Approve
								</Button>
							)}
							{canReject && (
								<Button
									variant="destructive"
									onClick={handleOpenRejectDialog}
									className="w-full"
								>
									<Ban className="mr-2 h-4 w-4" />
									Reject
								</Button>
							)}
							{canCancel && (
								<Button
									variant="outline"
									onClick={handleOpenCancelDialog}
									className="w-full"
								>
									<XCircle className="mr-2 h-4 w-4" />
									Cancel
								</Button>
							)}
						</div>
					</>
				)}
			</EntityDrawer>

			<Dialog
				open={showApproveDialog}
				onOpenChange={(open) => !open && handleCloseApproveDialog()}
			>
				<DialogContent className="sm:max-w-[450px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<CheckCircle className="h-5 w-5" />
							Approve Withdrawal
						</DialogTitle>
						<DialogDescription>
							Approve this withdrawal request. This requires 2FA verification.
						</DialogDescription>
					</DialogHeader>

					{withdrawal && (
						<div className="space-y-4 py-4">
							<div className="rounded-lg border p-3 bg-muted/50 space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Amount</span>
									<span className="font-medium">
										{withdrawal.amount} {withdrawal.currency}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">USD Value</span>
									<span>${withdrawal.usdAmount}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">To</span>
									<span className="font-mono text-xs">
										{truncateAddress(withdrawal.recipientAddress)}
									</span>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="approval-notes">Notes (optional)</Label>
								<Textarea
									id="approval-notes"
									placeholder="Add any notes about this approval"
									value={approvalNotes}
									onChange={(e) => setApprovalNotes(e.target.value)}
									disabled={approveMutation.isPending}
									className="min-h-[60px]"
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor="approve-totp">2FA Code</Label>
								<Input
									id="approve-totp"
									placeholder="6-digit code"
									value={totpCode}
									onChange={(e) =>
										setTotpCode(e.target.value.replace(/\D/g, ""))
									}
									disabled={approveMutation.isPending}
									maxLength={6}
									inputMode="numeric"
								/>
							</div>
						</div>
					)}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={handleCloseApproveDialog}
							disabled={approveMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleApprove}
							disabled={approveMutation.isPending || totpCode.length !== 6}
						>
							{approveMutation.isPending ? "Approving..." : "Approve"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={showRejectDialog}
				onOpenChange={(open) => !open && handleCloseRejectDialog()}
			>
				<DialogContent className="sm:max-w-[450px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Ban className="h-5 w-5" />
							Reject Withdrawal
						</DialogTitle>
						<DialogDescription>
							Reject this withdrawal request. Please provide a reason.
						</DialogDescription>
					</DialogHeader>

					{withdrawal && (
						<div className="space-y-4 py-4">
							<div className="rounded-lg border p-3 bg-muted/50 space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Amount</span>
									<span className="font-medium">
										{withdrawal.amount} {withdrawal.currency}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Requested By</span>
									<span>{withdrawal.createdBy.email}</span>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="reject-reason">
									Reason (min 10 characters)
								</Label>
								<Textarea
									id="reject-reason"
									placeholder="Why is this withdrawal being rejected?"
									value={rejectReason}
									onChange={(e) => setRejectReason(e.target.value)}
									disabled={rejectMutation.isPending}
									className="min-h-[80px]"
								/>
							</div>
						</div>
					)}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={handleCloseRejectDialog}
							disabled={rejectMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={handleReject}
							disabled={
								rejectMutation.isPending || rejectReason.trim().length < 10
							}
						>
							{rejectMutation.isPending ? "Rejecting..." : "Reject"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={showCancelDialog}
				onOpenChange={(open) => !open && handleCloseCancelDialog()}
			>
				<DialogContent className="sm:max-w-[400px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<XCircle className="h-5 w-5" />
							Cancel Withdrawal
						</DialogTitle>
						<DialogDescription>
							Cancel this withdrawal request. Only the creator can cancel.
						</DialogDescription>
					</DialogHeader>

					{withdrawal && (
						<div className="space-y-4 py-4">
							<div className="rounded-lg border p-3 bg-muted/50 space-y-2 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Amount</span>
									<span className="font-medium">
										{withdrawal.amount} {withdrawal.currency}
									</span>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="cancel-reason">Reason (optional)</Label>
								<Textarea
									id="cancel-reason"
									placeholder="Why is this being cancelled?"
									value={cancelReason}
									onChange={(e) => setCancelReason(e.target.value)}
									disabled={cancelMutation.isPending}
									className="min-h-[60px]"
								/>
							</div>
						</div>
					)}

					<DialogFooter>
						<Button
							variant="outline"
							onClick={handleCloseCancelDialog}
							disabled={cancelMutation.isPending}
						>
							Close
						</Button>
						<Button
							variant="destructive"
							onClick={handleCancel}
							disabled={cancelMutation.isPending}
						>
							{cancelMutation.isPending ? "Cancelling..." : "Cancel Withdrawal"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};
